import { useEffect, useMemo } from "react";
import type { AxiosError } from "axios";
import { Trans, useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { X, AlertTriangle, Loader2, RotateCcw } from "lucide-react";

import { Button } from "@/ui/components/ui/button";
import { Progress } from "@/ui/components/ui/progress";
import { cn } from "@/ui/lib/utils";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";

import PlanPreview from "./plan-preview";
import ChunkProgressList from "./chunk-progress-list";
import { useGetProjects, usePlanExportRun } from "../../api";
import { useChunkedExportRun } from "../../hooks/useChunkedExportRun";
import {
  chunkImageIds,
  resolveRunChunkSize,
  IMAGE_LIMIT_WARNING_CODE,
  type RunCreateInput,
} from "../../types/run";
import {
  buildTaskKindTabs,
  isProjectSelectable,
  projectUid,
  type ExportProject,
} from "../../types/project";
import type { TaxonomyFamily } from "../../types/taxonomy";

/**
 * Pull the backend's specific `error.detail` off an axios error (the API wraps
 * failures as `{ error: { detail, code, ... } }`, e.g. a `project_misconfigured`
 * plan rejection). Returns null when there's no structured detail so callers can
 * fall back to a generic message.
 */
const getApiErrorDetail = (err: unknown): string | null => {
  const apiError = (
    err as AxiosError<{ error?: { detail?: string; code?: string } }>
  )?.response?.data?.error;
  return apiError?.detail ?? null;
};

interface ExportPanelProps {
  /** Every selected image id — the full export selection. */
  imageIds: number[];
  /** Selected images that are not in the current view. */
  hiddenSelected: number;
  scopeLocationId?: number;
  scopeMachineIds: Set<number>;
  families?: TaxonomyFamily[];
  labelColors: Record<string, string>;
  /** Composite project id (project_key + destination); owned by the parent so
   *  the choice survives collapsing the panel. */
  selectedUid: string | null;
  onSelectUid: (uid: string | null) => void;
  /** Active task-kind tab; owned by the parent alongside the project choice. */
  activeTaskKind: string;
  onTaskKindChange: (kind: string) => void;
  onClose: () => void;
  /** All runs started — the parent clears the selection and collapses. */
  onExported: () => void;
}

const ExportPanel = ({
  imageIds,
  hiddenSelected,
  scopeLocationId,
  scopeMachineIds,
  families,
  labelColors,
  selectedUid,
  onSelectUid,
  activeTaskKind,
  onTaskKindChange,
  onClose,
  onExported,
}: ExportPanelProps) => {
  const { t } = useTranslation("mlExports");
  // `task_kind` is optional on /projects/, so fetch every kind once and group
  // client-side — that gives the tabs and their counts without N requests.
  const { data: projects } = useGetProjects();
  const taskKindTabs = useMemo(() => buildTaskKindTabs(projects, t), [projects, t]);
  const visibleProjects = useMemo(
    () => (projects ?? []).filter((p) => p.task_kind === activeTaskKind),
    [projects, activeTaskKind],
  );
  const selectedProject = useMemo<ExportProject | null>(
    () => visibleProjects.find((p) => projectUid(p) === selectedUid) ?? null,
    [visibleProjects, selectedUid],
  );
  const planExport = usePlanExportRun();
  const chunkedExport = useChunkedExportRun();

  const planData = planExport.data;
  const planLoading = planExport.isPending;
  const planError = planExport.isError;
  // Surface the backend's specific reason (e.g. "export project … is not ready")
  // instead of a generic failure line.
  const planErrorMessage =
    getApiErrorDetail(planExport.error) ?? t("export.planFailed");

  // The plan is the source of truth for how to split the selection; the
  // destination cap is only the fallback until it arrives.
  const chunkSize = resolveRunChunkSize(
    planData?.limits,
    selectedProject?.destination,
  );
  const runChunks = useMemo(
    () => chunkImageIds(imageIds, chunkSize),
    [imageIds, chunkSize],
  );
  const willSplitRuns = runChunks.length > 1;
  const limitWarning = planData?.warnings?.find(
    (w) => w.code === IMAGE_LIMIT_WARNING_CODE,
  );
  const startedRuns = chunkedExport.chunks.filter(
    (c) => c.status === "success",
  ).length;
  const runProgressPercent =
    chunkedExport.chunks.length > 0
      ? Math.round((startedRuns / chunkedExport.chunks.length) * 100)
      : 0;
  const hasFailedRun = chunkedExport.chunks.some((c) => c.status === "failed");

  // The default tab may hold no projects at all — fall back to the first tab
  // that does so the panel is never stuck on an empty kind.
  useEffect(() => {
    if (taskKindTabs.length === 0) return;
    if (taskKindTabs.some((tab) => tab.kind === activeTaskKind)) return;
    onTaskKindChange(taskKindTabs[0].kind);
  }, [taskKindTabs, activeTaskKind, onTaskKindChange]);

  // Preselect the first ready project, and re-run on every tab switch so the
  // selection recovers instead of pointing at a card from the previous kind.
  useEffect(() => {
    if (selectedUid && visibleProjects.some((p) => projectUid(p) === selectedUid))
      return;
    const firstEnabled = visibleProjects.find(isProjectSelectable);
    onSelectUid(firstEnabled ? projectUid(firstEnabled) : null);
  }, [visibleProjects, selectedUid, onSelectUid]);

  // task_kind + destination are fixed by the selected project; we only ever
  // send project_key (not the legacy project_id alias).
  const buildRunInput = (
    project: ExportProject,
    ids: number[],
  ): RunCreateInput => {
    const machineIdsArray = [...scopeMachineIds];
    return {
      task_kind: project.task_kind,
      destination: project.destination,
      project_key: project.project_key,
      image_ids: ids,
      ...(machineIdsArray.length > 0
        ? { machine_ids: machineIdsArray }
        : scopeLocationId != null
          ? { location_id: scopeLocationId }
          : {}),
    };
  };

  // Compute the plan only once a project is selected (it needs destination +
  // project_key). Until then, clear any stale plan result so a previous
  // "Failed to compute plan" doesn't linger.
  useEffect(() => {
    if (imageIds.length === 0) return;
    if (!selectedProject) {
      planExport.reset();
      return;
    }
    // Plan the WHOLE selection, over the per-run cap included — the plan endpoint
    // answers with the limits and the chunk size the export should be split by.
    planExport.mutate(buildRunInput(selectedProject, imageIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, imageIds.length]);

  // A different target or a different selection retires a half-finished export:
  // retrying must never re-fire the run input the failed chunks were built from.
  const resetChunkedExport = chunkedExport.reset;
  useEffect(() => {
    resetChunkedExport();
  }, [selectedUid, imageIds.length, resetChunkedExport]);

  // A failure leaves the chunk list on screen with the backend's reason, so
  // there's nothing to toast — only a completed export closes the panel.
  const finishExport = (runCount: number) => {
    toast.success(
      runCount > 1
        ? t("export.toast.startedRuns", {
            runs: formatNumber(runCount),
            images: formatNumber(imageIds.length),
          })
        : t("export.toast.started", {
            images: formatNumber(imageIds.length),
          }),
    );
    chunkedExport.reset();
    onExported();
  };

  const handleStartExport = async () => {
    if (!selectedProject) return;
    const runCount = runChunks.length;
    const success = await chunkedExport.execute(
      buildRunInput(selectedProject, imageIds),
      chunkSize,
    );
    if (success) finishExport(runCount);
  };

  const handleRetryRuns = async () => {
    const runCount = runChunks.length;
    const success = await chunkedExport.retryFromFailed();
    if (success) finishExport(runCount);
  };

  return (
    <div className="border-b px-6 py-4 max-h-[50vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">
          {t("export.title", {
            count: imageIds.length,
            value: formatNumber(imageIds.length),
          })}
        </h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-accent">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        {taskKindTabs.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              {t("export.taskKind")}
            </div>
            <div className="inline-flex rounded-md border border-border overflow-hidden">
              {taskKindTabs.map((tab, index) => {
                const isActive = tab.kind === activeTaskKind;
                return (
                  <button
                    key={tab.kind}
                    onClick={() => onTaskKindChange(tab.kind)}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors",
                      index > 0 && "border-l border-border",
                      isActive
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {tab.label}
                    <span
                      className={cn(
                        "rounded px-1 py-0.5 text-[10px] font-semibold tabular-nums",
                        isActive
                          ? "bg-primary/15 text-foreground"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            {t("export.project")}
          </div>
          <div className="flex gap-2 flex-wrap">
            {visibleProjects.map((project) => {
              const uid = projectUid(project);
              const selectable = isProjectSelectable(project);
              const isSelected = selectedUid === uid;
              return (
                <button
                  key={uid}
                  disabled={!selectable}
                  onClick={() => onSelectUid(uid)}
                  className={cn(
                    "flex flex-col items-start px-3 py-2 rounded-md text-xs font-medium border transition-colors",
                    isSelected
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground border-border hover:border-primary/50",
                    !selectable && "opacity-40 cursor-not-allowed",
                  )}
                >
                  <span>{project.project_name}</span>
                  <span
                    className={cn(
                      "text-[10px] font-mono",
                      isSelected
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground/60",
                    )}
                  >
                    {project.destination_label || project.destination}
                  </span>
                  {project.project_external_id && (
                    <span
                      title={t("export.externalIdTitle", {
                        destination:
                          project.destination_label || project.destination,
                        id: project.project_external_id,
                      })}
                      className={cn(
                        "mt-1 max-w-[13rem] truncate rounded border border-dashed px-1 py-0.5 font-mono text-[10px]",
                        isSelected
                          ? "border-primary-foreground/50 text-primary-foreground/80"
                          : "border-amber-500/60 bg-amber-500/5 text-amber-600 dark:text-amber-400",
                      )}
                    >
                      {project.project_external_id}
                    </span>
                  )}
                  {!selectable && (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-muted border border-border">
                      {project.project_configured
                        ? t("export.unavailable")
                        : t("export.notConfigured")}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {visibleProjects.length === 0 && (
            <div className="text-xs text-muted-foreground">
              {t("export.noProjects")}
            </div>
          )}
        </div>

        {willSplitRuns && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              {limitWarning?.detail ?? (
                <Trans
                  t={t}
                  i18nKey="export.splitWarning"
                  values={{
                    images: formatNumber(imageIds.length),
                    runs: formatNumber(runChunks.length),
                    size: formatNumber(chunkSize),
                  }}
                  components={{ runs: <span className="font-semibold" /> }}
                />
              )}
            </span>
          </div>
        )}

        <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1">
          <div className="font-semibold uppercase tracking-wide text-muted-foreground">
            {t("export.planPreview")}
          </div>
          {!selectedProject ? (
            <div className="text-muted-foreground">
              {t("export.selectProject")}
            </div>
          ) : (
            <>
              {planLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> {t("export.computingPlan")}
                </div>
              )}
              {planError && (
                <div className="text-destructive">{planErrorMessage}</div>
              )}
              {planData && !planLoading && (
                <PlanPreview
                  plan={planData}
                  families={families}
                  labelColors={labelColors}
                  destination={selectedProject?.destination}
                />
              )}
            </>
          )}
        </div>

        {hiddenSelected > 0 && (
          <div className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {t("export.hiddenIncluded", {
              value: formatNumber(hiddenSelected),
            })}
          </div>
        )}
      </div>

      {chunkedExport.chunks.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t("export.runsStarted", {
                count: chunkedExport.chunks.length,
                started: formatNumber(startedRuns),
                total: formatNumber(chunkedExport.chunks.length),
              })}
            </span>
            <span className="tabular-nums">
              {t("export.percent", { value: formatNumber(runProgressPercent) })}
            </span>
          </div>
          <Progress value={runProgressPercent} className="h-1.5" />
          <ChunkProgressList chunks={chunkedExport.chunks} unit="run" />
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          disabled={chunkedExport.isRunning}
        >
          {t("export.cancel")}
        </Button>
        {hasFailedRun ? (
          <Button
            size="sm"
            disabled={chunkedExport.isRunning}
            onClick={handleRetryRuns}
          >
            {chunkedExport.isRunning ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            )}
            {t("export.retryFailed")}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={
              !selectedProject ||
              !isProjectSelectable(selectedProject) ||
              chunkedExport.isRunning ||
              planLoading ||
              planError ||
              !planData ||
              planData.overview.total_candidates === 0 ||
              planData.selector?.yielded === 0
            }
            onClick={handleStartExport}
          >
            {chunkedExport.isRunning && (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            )}
            {chunkedExport.isRunning
              ? t("export.startingRuns")
              : willSplitRuns
                ? t("export.startRuns", { value: formatNumber(runChunks.length) })
                : t("export.startExport")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ExportPanel;
